import { useUnit } from 'effector-react';
import { useQuery } from '@tanstack/react-query';
import styled, { css, keyframes } from 'styled-components';
import {
  Container,
  Em,
  Button,
  Arrow,
  SectionLabel,
  SectionTitle,
  SectionLede,
} from '@/components/primitives';
import { navigated } from '@/model/router';
import { $verified, verifiedSet } from '@/model/kyc';
import { $themeName, themeToggled } from '@/model/theme';
import { modalOpened } from '@/model/modals';
import { toastSent } from '@/model/toasts';
import { queryFn } from '@/api';
import type { LandingMeta, OrbTag } from '@/api/mocks/lume';
import type { Theme } from '@/theme';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 4px var(--ring); }
  50%      { box-shadow: 0 0 0 8px transparent; }
`;
const breathe = keyframes`
  0%, 100% { transform: scale(1); } 50% { transform: scale(1.035); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const View = styled.section`
  position: relative;
  overflow: hidden;
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(700px 400px at 88% 12%, ${({ theme }) => theme.fx.ambHalo}, transparent 60%),
      radial-gradient(500px 320px at 12% 78%, ${({ theme }) => theme.fx.ambAurora}, transparent 65%);
    pointer-events: none;
  }
`;

const Hero = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing[14]};
  padding-bottom: ${({ theme }) => theme.spacing[13]};
  position: relative;
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: ${({ theme }) => theme.spacing[11]};
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  --ring: ${({ theme }) => theme.fx.ringHalo};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tone.halo.base};
  box-shadow: 0 0 0 4px ${({ theme }) => theme.fx.ringHalo};
  animation: ${pulse} 2.6s ${({ theme }) => theme.motion.easing.out} infinite;
`;

const HeroTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.displayMd};
  line-height: ${({ theme }) => theme.typography.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.xtight};
  font-variation-settings: 'opsz' 72;
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 1100px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.displaySm};
  }
  @media (max-width: 720px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.hLg};
    br {
      display: none;
    }
  }
`;

const Lede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[2]};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  max-width: ${({ theme }) => theme.widths.reading};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const CtaRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const VerifiedRow = styled.div<{ $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
  flex-wrap: wrap;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  color: ${({ theme }) => theme.ink[3]};
`;

const Tick = styled.span<{ $done?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: 11px;
  ${({ $done, theme }) =>
    $done
      ? css`
          background: ${theme.tone.aurora.base};
          color: ${theme.surface.elev};
          box-shadow: 0 0 0 4px ${theme.fx.ringAurora};
        `
      : css`
          background: transparent;
          border: 1.5px dashed ${theme.ink[4]};
        `}
`;

const CtaLink = styled.button`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSizes.uiLg};
  letter-spacing: 0;
  color: ${({ theme }) => theme.tone.halo.base};
  border-bottom: 1px solid ${({ theme }) => theme.tone.halo.line};
  padding: 0;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.duration.fast}
    ${({ theme }) => theme.motion.easing.out};

  &:hover {
    border-color: ${({ theme }) => theme.tone.halo.base};
  }
`;

const Meta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[6]};
  padding-top: ${({ theme }) => theme.spacing[9]};
  margin-top: ${({ theme }) => theme.spacing[10]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  span {
    display: block;
    color: ${({ theme }) => theme.ink[3]};
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
    text-transform: uppercase;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }
  strong {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
  }
`;

const OrbStage = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  aspect-ratio: 1 / 1;
  margin-left: auto;

  @media (max-width: 900px) {
    margin: 0 auto;
    max-width: min(420px, 100%);
  }
`;

const Orb = styled.div`
  position: absolute;
  inset: 14%;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? `
        radial-gradient(
          circle at 30% 26%,
          rgba(255, 255, 255, 0.6) 0%,
          rgba(255, 255, 255, 0) 32%
        ),
        radial-gradient(
          circle at 38% 32%,
          ${theme.tone.halo.glow} 0%,
          ${theme.tone.halo.base} 45%,
          ${theme.tone.halo.mid} 95%
        )
      `
      : `
        radial-gradient(
          circle at 30% 26%,
          rgba(255, 255, 255, 0.9) 0%,
          rgba(255, 255, 255, 0) 32%
        ),
        radial-gradient(
          circle at 38% 32%,
          ${theme.tone.halo.glow} 0%,
          ${theme.tone.halo.mid} 50%,
          ${theme.tone.halo.base} 95%
        )
      `};
  box-shadow:
    inset -18px -26px 56px ${({ theme }) => theme.fx.insetLo},
    inset 18px 26px 56px
      ${({ theme }) => (theme.name === 'dark' ? theme.fx.insetHi : 'rgba(255, 255, 255, 0.55)')},
    0 30px 80px -20px ${({ theme }) => theme.fx.glowHalo};
  animation: ${breathe} 6s ${({ theme }) => theme.motion.easing.out} infinite;

  &::after {
    content: '';
    position: absolute;
    inset: 12%;
    border-radius: 50%;
    background: radial-gradient(
      circle at 30% 30%,
      ${({ theme }) => (theme.name === 'dark' ? theme.fx.insetHiSoft : 'rgba(255, 255, 255, 0.6)')},
      transparent 55%
    );
    mix-blend-mode: ${({ theme }) => (theme.name === 'dark' ? 'overlay' : 'screen')};
    pointer-events: none;
  }
`;

const OrbRing = styled.div<{ $outer?: boolean }>`
  position: absolute;
  inset: ${({ $outer }) => ($outer ? '-3%' : '6%')};
  border-radius: 50%;
  border: 1px dashed
    ${({ theme, $outer }) =>
      $outer ? theme.line.line : theme.name === 'dark' ? 'rgba(129, 140, 248, 0.35)' : theme.tone.halo.soft};
  animation: ${spin} ${({ $outer }) => ($outer ? '120s' : '80s')} linear infinite;
  ${({ $outer }) =>
    $outer &&
    css`
      animation-direction: reverse;
    `}
`;

const OrbTag = styled.div<{ $pos: 't1' | 't2' | 't3' }>`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[2]};
  box-shadow: ${({ theme }) => theme.shadows.sh1};

  ${({ $pos }) =>
    $pos === 't1'
      ? css`
          top: 6%;
          right: -4%;
          @media (max-width: 720px) {
            top: 4%;
            right: 4%;
          }
        `
      : $pos === 't2'
        ? css`
            top: 44%;
            left: -10%;
            @media (max-width: 720px) {
              left: 4%;
            }
          `
        : css`
            bottom: 10%;
            right: 2%;
            @media (max-width: 720px) {
              bottom: 6%;
              right: 4%;
            }
          `}
`;

const TagDot = styled.span<{ $color: 'aurora' | 'solar' | 'mist' }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ theme, $color }) => theme.tone[$color].base};
`;

const HowItWorks = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing[14]};
  padding-bottom: ${({ theme }) => theme.spacing[14]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing[5]};

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StepTag = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  margin-top: ${({ theme }) => theme.spacing[5]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
`;

const StepCard = styled.div`
  padding: ${({ theme }) => theme.spacing[8]};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  transition:
    transform ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    border-color ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.ink[4]};
  }
`;

const StepNum = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  color: ${({ theme }) => theme.tone.halo.base};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StepTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hSm};
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-variation-settings: 'opsz' 48;

  em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: ${({ theme }) => theme.tone.halo.base};
    font-variation-settings: normal;
  }
`;

const StepText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
  color: ${({ theme }) => theme.ink[2]};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const ThemePair = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing[14]};
  padding-bottom: ${({ theme }) => theme.spacing[14]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
`;

const ThemePairGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[5]};
  margin-top: ${({ theme }) => theme.spacing[9]};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Sample = styled.div<{ $mode: 'light' | 'dark' }>`
  ${({ $mode, theme }) => sampleTheme($mode === 'light' ? lightSampleTheme(theme) : darkSampleTheme(theme))}
  position: relative;
  padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[8]} ${theme.spacing[7]}`};
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(420px 260px at 92% 8%, var(--amb-halo), transparent 65%),
      radial-gradient(320px 220px at 8% 92%, var(--amb-aurora), transparent 70%);
    pointer-events: none;
    z-index: -1;
  }
  &::after {
    content: '${({ $mode }) => ($mode === 'light' ? 'light' : 'dark')}';
    position: absolute;
    top: ${({ theme }) => theme.spacing[4]};
    right: ${({ theme }) => theme.spacing[4]};
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoXs};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
    text-transform: uppercase;
    padding: 3px ${({ theme }) => theme.spacing[2]};
    border-radius: ${({ theme }) => theme.radii.xs};
    background: var(--bg-deep);
    color: var(--ink-4);
  }
`;

const sampleTheme = (vars: Record<string, string>) => css`
  ${Object.entries(vars)
    .map(([k, v]) => `${k}: ${v};`)
    .join('\n')}
  background: var(--bg-elev);
  color: var(--ink);
  border: 1px solid var(--line);
`;

const lightSampleTheme = (_t: Theme) => ({
  '--bg': '#FBFBFD',
  '--bg-elev': '#FFFFFF',
  '--bg-deep': '#F2F2F5',
  '--ink': '#0A0A0C',
  '--ink-2': '#3A3A40',
  '--ink-3': '#76767E',
  '--ink-4': '#AEAEB6',
  '--line': '#E5E5EA',
  '--line-soft': '#F0F0F4',
  '--halo': '#818CF8',
  '--halo-deep': '#6366F1',
  '--halo-glow': '#C7D2FE',
  '--amb-halo': 'rgba(99, 102, 241, 0.08)',
  '--amb-aurora': 'rgba(110, 231, 183, 0.08)',
});

const darkSampleTheme = (_t: Theme) => ({
  '--bg': '#0E0F12',
  '--bg-elev': '#181A1F',
  '--bg-deep': '#08090B',
  '--ink': '#F5F5F7',
  '--ink-2': '#C7C7CC',
  '--ink-3': '#8E8E93',
  '--ink-4': '#5A5A60',
  '--line': '#26272D',
  '--line-soft': '#1C1D22',
  '--halo': '#A5B4FC',
  '--halo-deep': '#818CF8',
  '--halo-glow': '#C7D2FE',
  '--amb-halo': 'rgba(165, 180, 252, 0.14)',
  '--amb-aurora': 'rgba(110, 231, 183, 0.1)',
});

const SampleEyebrow = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SampleH3 = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
  line-height: ${({ theme }) => theme.typography.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  font-variation-settings: 'opsz' 66;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: var(--halo);
  }
`;

const SampleLede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  color: var(--ink-2);
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const SampleArch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) =>
    `${theme.spacing[2]} ${theme.spacing[5]} ${theme.spacing[2]} ${theme.spacing[2]}`};
  background: var(--bg-deep);
  border: 1px solid var(--line);
  border-radius: ${({ theme }) => theme.radii.pill};
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  .orb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, var(--halo-glow), var(--halo) 60%, var(--halo-deep));
  }
  .lbl {
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
    text-transform: uppercase;
    color: var(--ink-3);
  }
  .nm {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-size: ${({ theme }) => theme.typography.fontSizes.body};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
  }
  .nm em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: var(--halo);
  }
`;

const SampleMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[5]};
  border-top: 1px solid var(--line-soft);

  > div span {
    display: block;
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoXs};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
    text-transform: uppercase;
    color: var(--ink-3);
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }
  > div strong {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
  }
  > div strong em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: var(--halo);
  }
`;

const ThemePairFoot = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[9]};
  align-items: center;
  flex-wrap: wrap;
`;

const Hint = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  color: ${({ theme }) => theme.ink[3]};

  .k {
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoXs};
    color: ${({ theme }) => theme.ink[2]};
    background: ${({ theme }) => theme.surface.deep};
    border: 1px solid ${({ theme }) => theme.line.line};
    padding: 1px ${({ theme }) => theme.spacing[2]};
    border-radius: 4px;
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

export const Landing = () => {
  const [verified, themeName] = useUnit([$verified, $themeName]);

  const metaQuery = useQuery({
    queryKey: ['/api/lume/landing/meta'],
    queryFn: queryFn<LandingMeta>('/api/lume/landing/meta'),
    staleTime: Infinity,
  });
  const orbTagsQuery = useQuery({
    queryKey: ['/api/lume/landing/orb-tags'],
    queryFn: queryFn<OrbTag[]>('/api/lume/landing/orb-tags'),
    staleTime: Infinity,
  });

  const handleVerifiedCta = () => {
    if (verified) {
      verifiedSet(false);
      toastSent('Верификация сброшена');
    } else {
      navigated('kyc');
    }
  };

  return (
    <View>
      <Hero>
        <div>
          <Eyebrow>
            <Dot />
            Lume · спокойная оптика
          </Eyebrow>
          <HeroTitle>
            Цифровой <Em>след</Em>,
            <br />
            собранный <Em>один раз</Em>.
          </HeroTitle>
          <Lede>
            Lume автоматически собирает ваш цифровой след из сотен открытых источников, проверяет факты
            NLP-движком и держит отчёт актуальным круглосуточно. Никаких ручных анкет — только то, что уже
            есть в сети.
          </Lede>
          <CtaRow>
            <Button onClick={() => navigated('onboard')}>
              Получить отчёт <Arrow>→</Arrow>
            </Button>
            <Button $variant="ghost" onClick={() => modalOpened({ kind: 'example' })}>
              Посмотреть пример
            </Button>
          </CtaRow>

          <VerifiedRow $done={verified}>
            <Tick $done={verified}>{verified ? '✓' : ''}</Tick>
            <span>
              {verified
                ? 'Личность подтверждена · отчёт можно получить без повторной проверки'
                : 'Личность не подтверждена · потребуется один шаг проверки'}
            </span>
            <CtaLink onClick={handleVerifiedCta}>{verified ? 'сбросить →' : 'подтвердить →'}</CtaLink>
          </VerifiedRow>

          <Meta>
            {(metaQuery.data?.items ?? []).map((it) => (
              <MetaItem key={it.label}>
                <span>{it.label}</span>
                <strong>
                  {it.head}
                  {it.accent && <Em>{it.accent}</Em>}
                  {it.tail}
                </strong>
              </MetaItem>
            ))}
          </Meta>
        </div>

        <OrbStage>
          <OrbRing $outer />
          <OrbRing />
          <Orb />
          {(orbTagsQuery.data ?? []).slice(0, 3).map((tag, i) => (
            <OrbTag key={tag.label} $pos={(['t1', 't2', 't3'] as const)[i]!}>
              <TagDot $color={tag.tone} /> {tag.label}
            </OrbTag>
          ))}
        </OrbStage>
      </Hero>

      <HowItWorks>
        <SectionLabel>четыре шага</SectionLabel>
        <SectionTitle>
          От <Em>сигнала</Em> к отчёту.
        </SectionTitle>
        <SectionLede>
          Цикл Lume полностью автоматизирован: цифровой след собирается, анализируется и обновляется без
          участия человека. Вы получаете готовый, поддерживаемый отчёт.
        </SectionLede>
        <StepsGrid>
          {STEPS.map((s) => (
            <StepCard key={s.num}>
              <StepNum>
                {s.num} — {s.code}
              </StepNum>
              <StepTitle dangerouslySetInnerHTML={{ __html: s.title }} />
              <StepText>{s.text}</StepText>
              <StepTag>{s.tag}</StepTag>
            </StepCard>
          ))}
        </StepsGrid>
      </HowItWorks>

      <ThemePair>
        <SectionLabel>свет и тень</SectionLabel>
        <SectionTitle>
          Одинаково <Em>внимательно</Em> к свету и тьме.
        </SectionTitle>
        <SectionLede>
          Lume работает в двух режимах — светлом и тёмном. Те же токены, тот же ритм: меняется только
          поверхность. Ниже — один и тот же фрагмент отчёта в обеих темах.
        </SectionLede>

        <ThemePairGrid>
          {(['light', 'dark'] as const).map((mode) => (
            <Sample key={mode} $mode={mode}>
              <SampleEyebrow>превью · {mode === 'light' ? 'светлая' : 'тёмная'}</SampleEyebrow>
              <SampleH3>
                Архитектор <em>тишины</em>.
              </SampleH3>
              <SampleLede>
                Ваш цифровой голос точен и спокоен. Вы редко повышаете тон, но вас слышно: аудитория ценит
                ремесло и предсказуемость.
              </SampleLede>
              <SampleArch>
                <span className="orb" />
                <span className="lbl">архетип</span>
                <span className="nm">
                  Архитектор <em>тишины</em>
                </span>
              </SampleArch>
              <SampleMeta>
                <div>
                  <span>оценка</span>
                  <strong>
                    <em>78</em>/100
                  </strong>
                </div>
                <div>
                  <span>сигналов</span>
                  <strong>
                    <em>14</em> точн.
                  </strong>
                </div>
                <div>
                  <span>темп</span>
                  <strong>
                    <em>4.2</em>/мес.
                  </strong>
                </div>
              </SampleMeta>
            </Sample>
          ))}
        </ThemePairGrid>

        <ThemePairFoot>
          <Button $variant="ghost" $size="sm" onClick={() => themeToggled()}>
            Переключить тему <Arrow>→</Arrow>
          </Button>
          <Hint>
            <span className="k">T</span>
            горячая клавиша · сейчас {themeName === 'dark' ? 'тёмная' : 'светлая'}
          </Hint>
        </ThemePairFoot>
      </ThemePair>
    </View>
  );
};

const STEPS = [
  {
    num: '01',
    code: 'COLLECT',
    title: '<em>Сбор</em>',
    text: 'Парсинг сотен веб-ресурсов и подключение к открытым API. Соцсети, репозитории, публикации, упоминания.',
    tag: 'источники · 24/7',
  },
  {
    num: '02',
    code: 'ANALYZE',
    title: '<em>Анализ</em>',
    text: 'NLP-движок и ML-модели размечают и категоризируют сигналы: тон, темы, частота, влияние, аномалии.',
    tag: 'движок · NLP + ML',
  },
  {
    num: '03',
    code: 'MONITOR',
    title: '<em>Мониторинг</em>',
    text: 'Постоянное отслеживание изменений. Уведомления о новых публикациях, упоминаниях и аномалиях.',
    tag: 'алерты · realtime',
  },
  {
    num: '04',
    code: 'DELIVER',
    title: '<em>Отчёт</em>',
    text: 'Интерактивный портрет: профиль, источники, компетенции, инсайты. Публичная ссылка или API-доступ.',
    tag: 'формат · web · api',
  },
];
